import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Container, Row, Col } from 'react-bootstrap';
import { User, Mail, Phone, MapPin, Edit, Save, X } from 'lucide-react';
import { State } from 'country-state-city';
import { useTranslation } from 'react-i18next';
import config from '../../config';

// Composants séparés pour une meilleure organisation
const PasswordField = ({ 
  register, 
  errors, 
  isVisible, 
  onToggleVisibility, 
  language,
  t,
  fieldName = 'password',
  validation = {},
  label,
  placeholder
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label || t('password')}
    </label>
    <div className="relative">
      <input
        type={isVisible ? 'text' : 'password'}
        {...register(fieldName, validation)}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          errors[fieldName] ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder={placeholder || t('placeholders.password')}
      />
      <button
        type="button"
        className={`absolute inset-y-0 flex items-center px-4 text-gray-600 ${language === "ar" ? "left-0" : "right-0"}`}
        onClick={onToggleVisibility}
      >
        <EyeIcon isVisible={isVisible} />
      </button>
    </div>
    {errors[fieldName] && (
      <p className="text-red-500 text-sm mt-1">{errors[fieldName].message}</p>
    )}
  </div>
);

const EyeIcon = ({ isVisible }) => (
  isVisible ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  )
);

const FormField = ({ 
  label, 
  icon: Icon, 
  isEditing, 
  register, 
  errors, 
  fieldName, 
  validation,
  children,
  displayValue 
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {Icon && <Icon className="h-4 w-4 inline mr-2" />}
      {label}
    </label>
    {isEditing ? (
      children || (
        <input
          type="text"
          {...register(fieldName, validation)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors[fieldName] ? 'border-red-500' : 'border-gray-300'
          }`}
        />
      )
    ) : (
      <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
        {displayValue || '-----'}
      </p>
    )}
    {errors[fieldName] && (
      <p className="text-red-500 text-sm mt-1">{errors[fieldName].message}</p>
    )}
  </div>
);

const SelectField = ({
  label,
  icon: Icon,
  isEditing,
  register,
  errors,
  fieldName,
  validation,
  options,
  getOptionLabel,
  placeholder,
  disabled = false,
  watch, // Ajout de watch comme prop
  value // Pour le gouvernorat
}) => (
  <FormField
    label={label}
    icon={Icon}
    isEditing={isEditing}
    register={register}
    errors={errors}
    fieldName={fieldName}
    validation={validation}
    displayValue={getOptionLabel ? getOptionLabel(options.find(opt => 
      opt._id === watch(fieldName) || opt.name === value
    )) : ''}
  >
    {fieldName === 'adresse' ? (
      <select
        value={value}
        onChange={(e) => {
          // Cette fonction sera passée depuis le parent
          if (window.handleAddressChange) {
            window.handleAddressChange(e);
          }
        }}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          errors[fieldName] ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option._id || option.isoCode} value={option.name}>
            {getOptionLabel ? getOptionLabel(option) : option.name}
          </option>
        ))}
      </select>
    ) : (
      <select
        {...register(fieldName, validation)}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          errors[fieldName] ? 'border-red-500' : 'border-gray-300'
        }`}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option._id || option.isoCode} value={option._id || option.name}>
            {getOptionLabel ? getOptionLabel(option) : option.name}
          </option>
        ))}
      </select>
    )}
  </FormField>
);

// Composant Header séparé
const Header = ({ watch, isEditing, setIsEditing, handleSubmit, onSubmit, handleCancel, t }) => (
  <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-6 border-b border-blue-200">
    <div className="flex items-center justify-between grid grid-cols-1 min-md:grid-cols-2 gap-2">
      <div className="flex items-center space-x-4">
        <div className="bg-blue-900 rounded-full p-3">
          <User className="h-8 w-8 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 m-3">
            {watch('first_name_parent')} {watch('last_name_parent')}
          </h2>
        </div>
      </div>
      <div className="flex space-x-2 justify-end">
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 px-2 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>{t('edit')}</span>
          </button>
        ) : (
          <div className="grid grid-cols-1 min-md:grid-cols-2 gap-3">
            <button
              onClick={handleSubmit(onSubmit)}
              className="flex w-40 flex-row mx-4 items-center py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="h-4 w-4 mx-2" />
              <span>{t('save')}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex w-40 flex-row mx-4 items-center py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X className="h-4 w-4 mx-2" />
              <span>{t('cancel')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

const ComptClient = () => {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordVisibleConfirm, setIsPasswordVisibleConfirm] = useState(false);
  const [governorates, setGovernorates] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subLevels, setSubLevels] = useState([]);
  const [adresse, setAdresse] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      customer_phone: '',
      last_name_parent: '',
      first_name_parent: '',
      parent_phone: '',
      institution: '',
      level: '',
      sublevel: '',
      adresse: '',
      password: '',
      confirm_password: '',
    },
  });

  const password = watch('password');
  const token = localStorage.getItem('accessToken');
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  // Configuration des champs du formulaire
  const formFieldsConfig = {
    parent: [
      { name: 'last_name_parent', label: t('last_name_parent'), icon: User, required: true },
      { name: 'first_name_parent', label: t('first_name_parent'), icon: User, required: true },
      { 
        name: 'parent_phone', 
        label: t('parent_phone'), 
        icon: Phone, 
        required: true,
        pattern: { value: /^\d*$/, message: t('errors.phone_numeric_error') }
      }
    ],
    student: [
      { name: 'last_name', label: t('last_name'), icon: User, required: true },
      { name: 'first_name', label: t('first_name'), icon: User, required: true },
      { 
        name: 'customer_phone', 
        label: t('phone'), 
        icon: Phone,
        pattern: { value: /^\d*$/, message: t('errors.phone_numeric_error') }
      },
      { name: 'institution', label: t('institution'), icon: MapPin, required: true },
      { name: 'email', label: t('email'), icon: Mail, 
        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('errors.email_invalid') }
      }
    ]
  };

  // Initialisation des données
  useEffect(() => {
    const initializeData = async () => {
      const tunisiaGovernorates = State.getStatesOfCountry('TN');
      setGovernorates(tunisiaGovernorates || []);
      
      await fetchLevels();
      await fetchSubLevels();
    };

    initializeData();
  }, []);

  // Récupération du profil utilisateur
  useEffect(() => {
    fetchProfile();
  }, [setValue, t]);

  const fetchLevels = async () => {
    try {
      const response = await axios.get(`${config.SITE_API_URL}/active_level`, {
        headers: {
          'x-api-key': config.SITE_API_KEY,
          'Content-Type': 'application/json',
        },
      });
      setLevels(response?.data || []);
    } catch (error) {
      toast.error(t('errors.fetch_classes_error'));
    }
  };

  const fetchSubLevels = async () => {
    try {
      const response = await axios.get(`${config.SITE_API_URL}/actif_sublevel`, {
        headers: {
          'x-api-key': config.SITE_API_KEY,
          'Content-Type': 'application/json',
        },
      });
      setSubLevels(response?.data || []);
    } catch (error) {
      toast.error(t('errors.fetch_classes_error'));
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${config.SITE_API_URL}/user-profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-api-key': config.SITE_API_KEY,
        },
      });
      const user = response.data.customer;
      setUserFormValues(user);
    } catch (error) {
      toast.error(t('errors.fetch_profile_error') || 'Erreur lors de la récupération du profil');
    }
  };

  const setUserFormValues = useCallback((user) => {
    const fields = {
      'first_name': user.first_name_customer,
      'last_name': user.last_name_customer,
      'email': user.email,
      'customer_phone': user.customer_phone,
      'last_name_parent': user.last_name_parent,
      'first_name_parent': user.first_name_parent,
      'parent_phone': user.parent_phone,
      'institution': user.institution,
      'level': user.level?._id,
      'sublevel': user.sublevel?._id,
      'adresse': user.adresse?.replace(' Governorate', '')
    };

    Object.entries(fields).forEach(([field, value]) => {
      setValue(field, value || '');
    });
    
    setAdresse(user.adresse?.replace(' Governorate', '') || '');
  }, [setValue]);

  const filteredSubLevels = watch('level')
    ? subLevels.filter((item) => item.level?._id === watch('level') || item.level === watch('level'))
    : [];

  const onSubmit = async (data) => {
    try {
      const { confirm_password, ...updateData } = data;
      if (!updateData.email) delete updateData.email;
      updateData.adresse = adresse;

      const response = await axios.put(
        `${config.SITE_API_URL}/update_customer`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY,
          },
        }
      );

      toast.success(t('success.profile_updated') || response.data.message, { autoClose: 5000 });
      setIsEditing(false);
    } catch (error) {
      toast.error(
        t('errors.update_profile_error') || error.response?.data?.message || 'Erreur lors de la mise à jour du profil',
        { autoClose: 5000 }
      );
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProfile(); // Recharger les données originales
  };

  const onChangeInputAdresse = (e) => {
    const value = e.target.value;
    setAdresse(value);
    setValue('adresse', value);
  };

  // Stocker la fonction dans window pour y accéder depuis le composant enfant
  useEffect(() => {
    window.handleAddressChange = onChangeInputAdresse;
    return () => {
      delete window.handleAddressChange;
    };
  }, []);

  const renderFormFields = (fields) => 
    fields.map((field) => (
      <FormField
        key={field.name}
        label={field.label}
        icon={field.icon}
        isEditing={isEditing}
        register={register}
        errors={errors}
        fieldName={field.name}
        validation={getValidationRules(field)}
        displayValue={watch(field.name) || '-----'}
      />
    ));

  const getValidationRules = (field) => {
    const rules = {};
    if (field.required) rules.required = t(`errors.${field.name}_required`);
    if (field.pattern) rules.pattern = field.pattern;
    return rules;
  };

  const getLevelName = (level) => level?.name?.[i18n.language] || level?.name?.fr;

  const getGovernorateLabel = (gov) => gov?.name?.replace(' Governorate', '');

  return (
    <div style={{ direction: direction }}>
      <section className="mx-auto px-4 py-32">
        <Container>
          <Row>
            <Col className="lg:col-span-2 w-[80%] mx-auto">
              <div className="bg-white rounded-xl border-1 border-blue-200 overflow-hidden shadow-lg">
                <Header 
                  watch={watch}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                  handleCancel={handleCancel}
                  t={t}
                />
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('personal_info')}</h3>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6">
                      {/* Informations parent */}
                      {renderFormFields(formFieldsConfig.parent)}
                      
                      {/* Gouvernorat */}
                      <SelectField
                        label={t('governorate')}
                        icon={MapPin}
                        isEditing={isEditing}
                        register={register}
                        errors={errors}
                        fieldName="adresse"
                        options={governorates}
                        getOptionLabel={getGovernorateLabel}
                        placeholder={t('select_governorate')}
                        watch={watch}
                        value={adresse}
                      />

                      {/* Informations étudiant */}
                      {renderFormFields(formFieldsConfig.student)}

                      {/* Niveau et Classe */}
                      <div className="grid grid-cols-2 gap-2">
                        <SelectField
                          label={t('niveau')}
                          icon={MapPin}
                          isEditing={isEditing}
                          register={register}
                          errors={errors}
                          fieldName="level"
                          validation={{ required: t('errors.level_required') }}
                          options={levels}
                          getOptionLabel={getLevelName}
                          placeholder={t('select_niveau')}
                          watch={watch}
                        />
                        
                        <SelectField
                          label={t('class')}
                          icon={MapPin}
                          isEditing={isEditing}
                          register={register}
                          errors={errors}
                          fieldName="sublevel"
                          validation={{ required: t('errors.sublevel_required') }}
                          options={filteredSubLevels}
                          getOptionLabel={getLevelName}
                          placeholder={t('select_class')}
                          disabled={!watch('level')}
                          watch={watch}
                        />
                      </div>

                      {/* Champs mot de passe */}
                      {isEditing && (
                        <>
                          <PasswordField
                            register={register}
                            errors={errors}
                            isVisible={isPasswordVisible}
                            onToggleVisibility={() => setIsPasswordVisible(!isPasswordVisible)}
                            language={i18n.language}
                            t={t}
                            validation={{
                              required: t('errors.password_required') || 'Le mot de passe est requis',
                              minLength: {
                                value: 10,
                                message: t('errors.password_min_length') || 'Le mot de passe doit contenir au moins 10 caractères',
                              },
                              pattern: {
                                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/,
                                message: t('errors.password_requirements') ||
                                  'Le mot de passe doit contenir au moins une lettre majuscule, une lettre minuscule et un chiffre',
                              },
                            }}
                          />
                          
                          <PasswordField
                            register={register}
                            errors={errors}
                            fieldName="confirm_password"
                            isVisible={isPasswordVisibleConfirm}
                            onToggleVisibility={() => setIsPasswordVisibleConfirm(!isPasswordVisibleConfirm)}
                            language={i18n.language}
                            t={t}
                            validation={{
                              validate: (value) =>
                                !password || value === password || t('errors.password_mismatch'),
                            }}
                            label={t('confirm_password')}
                            placeholder={t('placeholders.confirm_password')}
                          />
                        </>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <ToastContainer
          position="bottom-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={i18n.language === 'ar'}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </section>
    </div>
  );
};

export default ComptClient;